import { createSlice, PayloadAction  } from "@reduxjs/toolkit";
import { SINGLE_RIDER_IDS_COVERAGE, SINGLE_RIDER_IDS_DISEASE, SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO, SINGLE_RIDER_IDS_GCI_TOP4, SINGLE_RIDER_IDS_GCIR } from "../../../constant/gms-constant";
import { RootState } from "../../../api/store";
import { AttachableRiderState, InputOptionGDR, RiderGDR, RiderGHIRandGAMERRandBB } from "./attachable-riders.state";

const initialState: AttachableRiderState = {
    selectedRiders: [],
    selectedGHIR: null,
    selectedGAMERR9: null,
    selectedGAMERR14: null,
    selectedBMSR: null,
    selectedGDR: null,
    selectedBMS: null,
    selectedGTPDRCR: null,

    notesForRiders: ''
};

export const attachableRidersSlice = createSlice({
    name: "attachableRidersSlice",
    initialState,
    reducers: {
        resetRiders: () => ({ ...initialState }),
        resetSelectedRiders(state) {
            state.selectedRiders = [];
            state.selectedGHIR = null;
            state.selectedGAMERR9 = null;
            state.selectedGAMERR14 = null;
            state.selectedBMSR = null;
            state.selectedGDR = null;
            state.selectedGTPDRCR = null;
        },
        updateRiderInput: {
            reducer(state, action: PayloadAction<{ riderId: number; index: number; value: number }>) {
                const { riderId, index, value } = action.payload;
                const rider = state.selectedRiders.find(r => r.id === riderId);

                if (!rider) return;

                // ensure inputs array exists
                if (!rider.inputs) rider.inputs = [];

                // update the specific input at index
                rider.inputs[index] = {
                    ...rider.inputs[index],
                    fAmount: value,
                };
            },
            prepare(riderId: number, index: number, value: number) {
                return { payload: { riderId, index, value } };
            },
        },

        toggleRider(state, action: PayloadAction<{ id: number; label: string }>) {
            const rider = action.payload;

            const exists = state.selectedRiders.some(r => r.id === rider.id);

            if (exists) {
                // Remove rider if it exists
                state.selectedRiders = state.selectedRiders.filter(r => r.id !== rider.id);
                return;
            }

            const singleSelectGroup: number[] = SINGLE_RIDER_IDS_COVERAGE;
            const singleSelectGCIR: number[] = SINGLE_RIDER_IDS_GCIR;
            const singleSelectDisease: number[] = SINGLE_RIDER_IDS_DISEASE;

            const singleSelectGCI_TOP4: number[] = SINGLE_RIDER_IDS_GCI_TOP4;
            const singleSelectGCI_DENGUE_MOSQUITO: number[] = SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO;

            if (singleSelectGroup.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGroup.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectGCIR.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGCIR.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectDisease.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectDisease.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectGCI_TOP4.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGCI_TOP4.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectGCI_DENGUE_MOSQUITO.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGCI_DENGUE_MOSQUITO.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else {
                state.selectedRiders.push({
                    id: rider.id,
                    label: rider.label
                });
            }
        },

        // ---- set special riders individually ----
        setGHIR(state, action: PayloadAction<RiderGHIRandGAMERRandBB | null>) {
            state.selectedGHIR = action.payload;
        },
        setGTPDRCR(state, action: PayloadAction<RiderGHIRandGAMERRandBB | null>) {
            state.selectedGTPDRCR = action.payload;
        },
        setGAMERR9(state, action: PayloadAction<RiderGHIRandGAMERRandBB | null>) {
            state.selectedGAMERR9 = action.payload;
        },
        setGAMERR14(state, action: PayloadAction<RiderGHIRandGAMERRandBB | null>) {
            state.selectedGAMERR14 = action.payload;
        },
        setBMSR(state, action: PayloadAction<RiderGHIRandGAMERRandBB | null>) {
            state.selectedBMSR = action.payload;
        },

        // ---- set GDR rider input ----
        setGDR(state, action: PayloadAction<RiderGDR | null>) {
            state.selectedGDR = action.payload;
        },
        toggleGDRInput(
            state,
            action: PayloadAction<{
                rider: { id: number; label: string };
                input: InputOptionGDR; // <-- updated type here
            }>
        ) {
            const { rider, input } = action.payload;
            state.selectedGDR = {
                id: rider.id,
                label: rider.label,
                inputs: [input],
            };
        },
        setSelectedRiders(state, action: PayloadAction<{ id: number; label: string; inputs?: any[] }[]>) {
            state.selectedRiders = action.payload;
        },

        setUpdateNotesForRider(state, action: PayloadAction<string>) {
            state.notesForRiders = action.payload;
        },
        seClearNotesForRider(state) {
            state.notesForRiders = '';
        },
    }
})

export const {
    resetRiders,
    resetSelectedRiders,
    toggleRider,
    setGHIR,
    setGAMERR9,
    setGAMERR14,
    setGTPDRCR,
    setBMSR,
    setGDR,
    toggleGDRInput,
    updateRiderInput,
    setSelectedRiders,
    
    setUpdateNotesForRider,
    seClearNotesForRider
} = attachableRidersSlice.actions;

export const selectedRidersState = (state: RootState) => state.attachableRidersSlice.selectedRiders;
export const selectedGHIRState = (state: RootState) => state.attachableRidersSlice.selectedGHIR;
export const selectedGAMERR9State = (state: RootState) => state.attachableRidersSlice.selectedGAMERR9;
export const selectedGAMERR14State = (state: RootState) => state.attachableRidersSlice.selectedGAMERR14;
export const selectedBMSRState = (state: RootState) => state.attachableRidersSlice.selectedBMSR;
export const selectedGDRState = (state: RootState) => state.attachableRidersSlice.selectedGDR;
export const selectedGTPDRCRState = (state: RootState) => state.attachableRidersSlice.selectedGTPDRCR;

export const selectNotesForRiderState = (state: RootState) => state.attachableRidersSlice.notesForRiders;

export default attachableRidersSlice.reducer;